// React
import { PicturesType } from "pages/Inventori/Transaction/AddNewItem/types"
import { useCallback, useRef, useState } from "react"

// Types
import type { SuccessFetch } from "types"

// Utils
import { useApi } from "utils"

export interface DataType {
  mr_unit_id: number
  mr_unit_name: string
  product_barcode: string
  product_foto: string
  product_id: number
  product_name: string
  different: number
  real_count: number
  soh_count: number,
  id_coa_buy: number,
  coa_buy_code: string,
  coa_buy_name: string,
  pictures: PicturesType[]
  unit_convention_name: string
  id_coa_asset: number
  coa_asset_code: string
  coa_asset_name: string,
  soh: string
  avg_price: number,
  quantity_unit_sell_convention: number
  buy:boolean,
  sell:boolean,
  inventory:boolean
}

export type LocationProductFilters = {
  mr_product_group_id?: string
  mr_product_category_id?: string
}

export function useLocationProduct(): {
  data: DataType[]
  isLoading: boolean
  refetch: (
    location_id: string,
    transaction_date: string,
    filters?: LocationProductFilters
  ) => void
} {
  // Hooks
  const api = useApi()
  const apiRef = useRef(api)
  apiRef.current = api
  const [data, setData] = useState<DataType[]>([])
  const [isLoading, setLoading] = useState<boolean>(false)

  const refetch = useCallback((
    location_id?: string,
    transaction_date?: string,
    filters?: LocationProductFilters
  ) => {
    setLoading(true)

    const params: Record<string, string> = {}
    if (location_id) params.location_id = location_id
    if (transaction_date) params.transaction_date = transaction_date
    if (filters?.mr_product_group_id) {
      params.mr_product_group_id = filters.mr_product_group_id
    }
    if (filters?.mr_product_category_id) {
      params.mr_product_category_id = filters.mr_product_category_id
    }

    apiRef.current.get("/invcount/product_by_location", { params })
    .then((res: SuccessFetch<{
      payload: {
        product_barcode: string
        product_foto: string
        product_id: number
        product_name: string
        soh: string
        unit_convention_id: number
        unit_convention_name: string
        unit_sell_measure_id: number
        unit_sell_measure_name: string,
        pictures: PicturesType[],
        id_coa_buy: number,
        coa_buy_code: string,
        coa_buy_name: string,
        id_coa_asset: number,
        coa_asset_code: string,
        coa_asset_name: string
        avg_price: number,
        quantity_unit_sell_convention: number
        buy:boolean,
        sell:boolean,
        inventory:boolean
      }[]
    }>) => {
      setData(res.data.payload.map(item => {
        // Vars
        const sell = item.sell

        return {
          real_count: 0,
          different: Number(item.soh),
          mr_unit_id: sell ? item.unit_sell_measure_id : item.unit_convention_id,
          mr_unit_name: sell ? item.unit_sell_measure_name : item.unit_convention_name,
          product_barcode: item.product_barcode,
          product_foto: item.product_foto,
          product_id: item.product_id,
          product_name: item.product_name,
          soh_count: sell ? Number(item.soh) / Number(item.quantity_unit_sell_convention) : Number(item.soh),
          pictures: item.pictures,
          unit_convention_name: item.unit_convention_name,
          coa_buy_code: item.coa_buy_code,
          coa_buy_name: item.coa_buy_name,
          id_coa_buy: item.id_coa_buy,
          coa_asset_code: item.coa_asset_code,
          coa_asset_name: item.coa_asset_name,
          id_coa_asset: item.id_coa_asset,
          soh: item.soh,
          avg_price: item.avg_price,
          quantity_unit_sell_convention: item.quantity_unit_sell_convention,
          buy:item.buy,
          sell:item.sell,
          inventory:item.inventory
        }
      }))
    }).catch(() => {
      setData([])
    }).finally(() => {
      setLoading(false)
    })
  }, [])

  return { data, isLoading, refetch }
}